import { useEffect, useState } from "react";
import { api } from "@/lib/trpc";

/** Пауза набора имени, после которой имеет смысл просить вариант ника. */
const DEBOUNCE_MS = 500;

/**
 * Ник, придуманный сервером (шаг 1 мастера).
 *
 * Ник обязателен, но человеку он в этот момент не нужен ни для чего: он пришёл
 * войти, а не выбирать себе имя в чужой экосистеме. Поэтому поле заполняем
 * сами свободным вариантом от почты, под которой он вошёл (`sergey@oxem.dev` →
 * `sergey`, занят — `sergey1`), а человек либо соглашается, либо стирает и
 * пишет своё.
 *
 * Запрос уходит и при пустом имени: почту сервер знает сам, и ник обязан
 * стоять в поле уже в момент открытия шага, а не после того, как человек
 * наберёт имя. Имя всё же передаём — оно запасная база на случай почты, из
 * которой латиницы не вышло (адрес на кириллице, IDN). Ответ от этого обычно
 * не меняется, поэтому поле не мигает при наборе имени.
 *
 * Запрос уходит ТОЛЬКО пока поле ника не тронуто (`enabled`): подставлять
 * вариант поверх того, что человек уже печатает, — способ отобрать у него поле.
 */
export function useNicknameSuggestion(name: string, enabled: boolean): string | null {
	const trimmed = name.trim();
	const [debounced, setDebounced] = useState(trimmed);

	useEffect(() => {
		const timer = setTimeout(() => setDebounced(trimmed), DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [trimmed]);

	const query = api.profile.suggestNickname.useQuery(
		{ from: debounced },
		{
			enabled,
			// Вариант детерминированный — перезапрашивать его нечего.
			staleTime: 5 * 60_000,
			retry: false,
		},
	);

	return debounced === trimmed ? (query.data?.nickname ?? null) : null;
}
